import React, { useState, useEffect } from 'react';
import { PlanningPhase, ParsedPlan } from '../helpers/aiPlanningParser';
import { Brain, CircleHelp, ClipboardList, ListTodo, Code2, type LucideIcon } from 'lucide-react';
import {
  THINKING_ICON,
  REASONING_ICON,
  PLANNING_ICON,
  STEPS_ICON,
  CODE_ICON,
} from '../helpers/phaseIcons';
import styles from './AiPhasePills.module.css';

export type PhaseId = PlanningPhase;
export type PhaseStatus = 'idle' | 'running' | 'done' | 'error';

export interface PhaseTab {
  id: PhaseId;
  label: string;
  status?: PhaseStatus;
}

interface AiPhasePillsProps {
  plan: ParsedPlan;
  activePhase: PlanningPhase | null; // The phase currently being streamed
  selectedPhase?: PhaseId; // The phase the user has selected to view
  onPhaseSelect?: (...args: [PhaseId]) => void;
  className?: string;
  hideStepsAndCode?: boolean; // Hide steps and code pills (when shown in separate tabs)
}

const PHASES: PhaseId[] = ['thinking', 'reasoning', 'planning', 'steps', 'code'];

const PHASE_LABELS: Record<PhaseId, string> = {
  thinking: 'Thinking',
  reasoning: 'Reasoning',
  planning: 'Planning',
  steps: 'To-dos',
  code: 'Coding',
};

const PHASE_HINTS: Record<PhaseId, string> = {
  thinking: 'Initial analysis and context gathering',
  reasoning: 'Decision logic and tradeoffs',
  planning: 'Execution plan and ordering',
  steps: 'Actionable task list',
  code: 'Implementation and file changes',
};

const PHASE_VECTOR_ICONS: Record<PhaseId, LucideIcon> = {
  thinking: Brain,
  reasoning: CircleHelp,
  planning: ClipboardList,
  steps: ListTodo,
  code: Code2,
};

export const PHASE_ICONS: Record<PhaseId, string> = {
  thinking: THINKING_ICON,
  reasoning: REASONING_ICON,
  planning: PLANNING_ICON,
  steps: STEPS_ICON,
  code: CODE_ICON,
};

export const AiPhasePills: React.FC<AiPhasePillsProps> = ({
  plan,
  activePhase,
  selectedPhase,
  onPhaseSelect,
  className,
  hideStepsAndCode = false,
}) => {
  const visiblePhases = hideStepsAndCode
    ? PHASES.filter((p) => p !== 'steps' && p !== 'code')
    : PHASES;

  const primaryPhases = visiblePhases.filter((p) => p !== 'steps' && p !== 'code');
  const secondaryPhases = visiblePhases.filter((p) => p === 'steps' || p === 'code');
  const activeIndex = activePhase ? PHASES.indexOf(activePhase) : -1;

  const [internalSelectedPhase, setInternalSelectedPhase] = useState<PhaseId>(
    selectedPhase || activePhase || 'thinking',
  );

  useEffect(() => {
    if (selectedPhase) {
      setInternalSelectedPhase(selectedPhase);
    } else if (activePhase) {
      setInternalSelectedPhase(activePhase);
    }
  }, [selectedPhase, activePhase]);

  const currentSelectedPhase = selectedPhase || internalSelectedPhase;

  const getPhaseStatus = (phase: PhaseId, index: number): PhaseStatus => {
    if (phase === activePhase) return 'running';

    const hasContent =
      phase === 'steps'
        ? (plan.steps?.length || 0) > 0
        : (plan[phase as keyof ParsedPlan] as string)?.length > 0;

    if (hasContent) {
      if (activeIndex !== -1 && index < activeIndex) return 'done';
      if (activePhase === null) return 'done';
    }

    return 'idle';
  };

  const getStatusLabel = (status: PhaseStatus, phase: PhaseId): string => {
    if (status === 'running') return 'In progress';
    if (status === 'done') return 'Complete';
    if (status === 'error') return 'Error';
    if (status === 'idle' && activePhase !== null && phase !== activePhase) return 'Queued';
    return 'Idle';
  };

  const handlePhaseClick = (phase: PhaseId) => {
    setInternalSelectedPhase(phase);
    if (onPhaseSelect) {
      onPhaseSelect(phase);
    }
  };

  const renderPill = (phase: PhaseId) => {
    const status = getPhaseStatus(phase, PHASES.indexOf(phase));
    const PhaseIcon = PHASE_VECTOR_ICONS[phase];
    const isSelected = phase === currentSelectedPhase;
    const label = PHASE_LABELS[phase];
    const hint = PHASE_HINTS[phase];
    const statusLabel = getStatusLabel(status, phase);

    return (
      <div key={phase} className={styles.pillColumn}>
        <button
          type="button"
          onClick={() => handlePhaseClick(phase)}
          className={`${styles.pill} ${isSelected ? styles.pillActive : ''} ${status === 'running' ? styles.pillRunning : ''} ${status === 'done' ? styles.pillDone : ''}`}
          disabled={status === 'idle' && activePhase !== null}
          title={`${label}: ${hint}`}
          aria-current={isSelected ? 'true' : 'false'}
          aria-label={`${label}. ${statusLabel}.`}
        >
          <PhaseIcon className={styles.pillIcon} size={16} strokeWidth={2} aria-hidden="true" />
          <span className={styles.pillLabelWrap}>
            <span className={styles.pillLabel}>{label}</span>
            {isSelected && <span className={styles.pillState}>{statusLabel}</span>}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.primaryRow}>{primaryPhases.map((phase) => renderPill(phase))}</div>
      {secondaryPhases.length > 0 && (
        <div className={styles.secondaryRow}>
          {secondaryPhases.map((phase) => renderPill(phase))}
        </div>
      )}
    </div>
  );
};
